import { FileUploadError } from "@contember/client";
import { toFileId } from "./toFileId.js";
import { assertNever } from "../utils/assertNever.js";
const initializeFileUploadState = () => ({
  fileIdByFile: /* @__PURE__ */ new WeakMap(),
  state: /* @__PURE__ */ new Map()
});
const fileUploadReducer = (previousState, action) => {
  let newState = void 0;
  switch (action.type) {
    case "initialize": {
      for (const [fileId, metadata] of action.files) {
        (newState ?? (newState = new Map(previousState.state))).set(fileId, {
          readyState: "initializing",
          abortController: metadata.abortController,
          previewUrl: metadata.previewUrl,
          file: metadata.file
        });
        previousState.fileIdByFile.set(metadata.file, fileId);
      }
      break;
    }
    case "startUploading": {
      for (const [fileOrId, metadata] of action.files) {
        const fileId = toFileId(previousState, fileOrId);
        const fileState = (newState ?? previousState.state).get(fileId);
        if (fileState === void 0 || fileState.readyState !== "initializing") {
          continue;
        }
        (newState ?? (newState = new Map(previousState.state))).set(fileId, {
          readyState: "uploading",
          abortController: fileState.abortController,
          file: fileState.file,
          metadata: metadata.metadata,
          previewUrl: fileState.previewUrl,
          progress: void 0,
          uploader: metadata.uploader
        });
      }
      break;
    }
    case "finishSuccessfully": {
      for (const [fileOrId, result] of action.result) {
        const fileId = toFileId(previousState, fileOrId);
        const previousFileState = (newState ?? previousState.state).get(fileId);
        if (previousFileState === void 0 || previousFileState.readyState !== "uploading") {
          continue;
        }
        (newState ?? (newState = new Map(previousState.state))).set(fileId, {
          readyState: "success",
          file: previousFileState.file,
          metadata: previousFileState.metadata,
          previewUrl: previousFileState.previewUrl,
          result,
          uploader: previousFileState.uploader
        });
      }
      break;
    }
    case "finishWithError": {
      for (const errorSpec of action.error) {
        let fileOrId;
        let rawError;
        if (Array.isArray(errorSpec)) {
          [fileOrId, rawError] = errorSpec;
        } else {
          fileOrId = errorSpec;
          rawError = void 0;
        }
        const errors = [];
        if (rawError instanceof FileUploadError) {
          errors.push(rawError);
        } else if (Array.isArray(rawError)) {
          for (const error of rawError) {
            if (error instanceof FileUploadError) {
              errors.push(error);
            }
          }
        }
        const fileId = toFileId(previousState, fileOrId);
        const previousFileState = (newState ?? previousState.state).get(fileId);
        if (previousFileState === void 0 || previousFileState.readyState !== "initializing" && previousFileState.readyState !== "uploading") {
          continue;
        }
        const uploader = previousFileState.readyState === "uploading" ? previousFileState.uploader : void 0;
        const metadata = previousFileState.readyState === "uploading" ? previousFileState.metadata : void 0;
        (newState ?? (newState = new Map(previousState.state))).set(fileId, {
          readyState: "error",
          errors: errors.length ? errors : void 0,
          rawError,
          file: previousFileState.file,
          metadata,
          previewUrl: previousFileState.previewUrl,
          uploader
        });
      }
      break;
    }
    case "purge": {
      for (const fileOrId of action.files) {
        const fileId = toFileId(previousState, fileOrId);
        const fileState = (newState ?? previousState.state).get(fileId);
        if (fileState === void 0) {
          continue;
        }
        URL.revokeObjectURL(fileState.previewUrl);
        if (fileState.readyState === "initializing" || fileState.readyState === "uploading") {
          fileState.abortController.abort();
        }
        (newState ?? (newState = new Map(previousState.state))).delete(fileId);
      }
      break;
    }
    case "updateUploadProgress": {
      for (const [fileOrId, { progress }] of action.progress) {
        const fileId = toFileId(previousState, fileOrId);
        const previousFileState = (newState ?? previousState.state).get(fileId);
        if (progress === void 0 || previousFileState === void 0 || previousFileState.readyState !== "uploading") {
          continue;
        }
        const roundedProgress = Math.floor(progress);
        if (roundedProgress === previousFileState.progress) {
          continue;
        }
        (newState ?? (newState = new Map(previousState.state))).set(fileId, {
          readyState: "uploading",
          file: previousFileState.file,
          uploader: previousFileState.uploader,
          abortController: previousFileState.abortController,
          previewUrl: previousFileState.previewUrl,
          metadata: previousFileState.metadata,
          progress: roundedProgress
        });
      }
      break;
    }
    default:
      assertNever();
  }
  if (newState === void 0) {
    return previousState;
  }
  return { ...previousState, state: newState };
};
export {
  fileUploadReducer,
  initializeFileUploadState
};
//# sourceMappingURL=fileUploadReducer.js.map
